import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Skeleton,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Divider,
  Stack,
  Switch,
} from '@mui/material';
import {
  Tune as TuneIcon,
  RestartAlt as RestartAltIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  WarningAmberRounded as WarningIcon,
} from '@mui/icons-material';
import ActionButton from '../components/ActionButton';
import faceRecognitionConfigService from '../services/faceRecognitionConfigService';

// ---------------------------------------------------------------------------
// Field definitions — display name, description, constraints, type
// ---------------------------------------------------------------------------
const FIELD_CONFIG = [
  {
    key: 'verification_threshold',
    label: 'Verification Threshold',
    description:
      'Minimum confidence score required to verify a user\'s identity during a life declaration check. Higher values increase security but may cause more false rejections.',
    type: 'percentage',     // stored as 0.50–1.00, displayed as 50–100 %
    min: 50,
    max: 100,
    step: 1,
    unit: '%',
    constraintLabel: '50 % – 100 %',
  },
  {
    key: 'enrollment_threshold',
    label: 'Enrollment Threshold',
    description:
      'Minimum confidence score required when enrolling a new face profile for a user. Controls the quality bar for initial face registration.',
    type: 'percentage',
    min: 50,
    max: 100,
    step: 1,
    unit: '%',
    constraintLabel: '50 % – 100 %',
  },
  {
    key: 'embedding_update_threshold',
    label: 'Embedding Update Threshold',
    description:
      'Minimum confidence score required when updating an existing face embedding. Prevents unauthorised updates to stored face data.',
    type: 'percentage',
    min: 50,
    max: 100,
    step: 1,
    unit: '%',
    constraintLabel: '50 % – 100 %',
  },
  {
    key: 'under_review_margin',
    label: 'Under-Review Margin',
    description:
      'Similarity margin below the verification threshold that places a submission under review instead of failing it outright. Provides a grace zone for borderline cases. Note: verification threshold minus this margin must remain ≥ 50 %.',
    type: 'percentage',     // stored as 0.01–0.20, displayed as 1–20 %
    min: 1,
    max: 20,
    step: 0.5,
    unit: '%',
    constraintLabel: '1 % – 20 %',
  },
  {
    key: 'liveness_challenges_count',
    label: 'Liveness Challenges Count',
    description:
      'Number of face liveness challenges a user must pass during verification. More challenges improve anti-spoofing protection at the cost of a longer verification flow.',
    type: 'integer',        // stored and displayed as 1–5
    min: 1,
    max: 5,
    step: 1,
    unit: '',
    constraintLabel: '1 – 5 challenges',
  },
];

// ---------------------------------------------------------------------------
// Conversion helpers (API ↔ display)
// ---------------------------------------------------------------------------
const toDisplay = (fieldDef, apiValue) => {
  if (apiValue === null || apiValue === undefined) return '';
  if (fieldDef.type === 'integer') return String(apiValue);
  // percentage: multiply by 100
  return String(Math.round(parseFloat(apiValue) * 100 * 10) / 10);
};

const toApiValue = (fieldDef, displayValue) => {
  if (fieldDef.type === 'integer') return parseInt(displayValue, 10);
  // percentage: divide by 100, round to 4 decimal places
  return parseFloat((parseFloat(displayValue) / 100).toFixed(4));
};

// ---------------------------------------------------------------------------
// Inline styles shared across the component
// ---------------------------------------------------------------------------
const inputStyles = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: 'white',
    '& fieldset': { borderColor: '#E2E8F0' },
    '&:hover fieldset': { borderColor: '#4E83BD' },
    '&.Mui-focused fieldset': { borderColor: '#4E83BD' },
  },
  '& .MuiInputBase-input': { fontSize: '14px', color: '#1F2937' },
};

const tableHeadCellSx = {
  backgroundColor: '#4E83BD',
  color: 'white',
  fontWeight: 700,
  fontSize: '13px',
  py: 1.5,
  whiteSpace: 'nowrap',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const ManageLifeDeclarationProcess = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Config state
  const [loading, setLoading] = useState(true);
  const [fieldValues, setFieldValues] = useState({}); // display values (string)
  const [fieldErrors, setFieldErrors] = useState({});
  const [metaInfo, setMetaInfo] = useState({ isActive: null, updatedAt: null, updatedBy: null });
  const [diagnosticSubmissionMode, setDiagnosticSubmissionMode] = useState(false);

  // Action loading flags (simple booleans to use with ActionButton)
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Reset confirmation dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // -------------------------------------------------------------------------
  // Load config on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    const result = await faceRecognitionConfigService.getConfig();
    if (result.success) {
      applyApiData(result.data);
    } else {
      showSnackbar(result.error, 'error');
    }
    setLoading(false);
  };

  const applyApiData = (data) => {
    const displayVals = {};
    FIELD_CONFIG.forEach((f) => {
      displayVals[f.key] = toDisplay(f, data[f.key]);
    });
    setFieldValues(displayVals);
    setFieldErrors({});
    setDiagnosticSubmissionMode(Boolean(data.diagnostic_submission_mode));
    setMetaInfo({
      isActive: data.is_active,
      updatedAt: data.updated_at,
      updatedBy: data.updated_by,
    });
  };

  // -------------------------------------------------------------------------
  // Field change handler
  // -------------------------------------------------------------------------
  const handleFieldChange = (key, value) => {
    setFieldValues((prev) => ({ ...prev, [key]: value }));
    // Clear error on edit
    if (fieldErrors[key]) {
      setFieldErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  // -------------------------------------------------------------------------
  // Client-side validation
  // -------------------------------------------------------------------------
  const validateFields = () => {
    const errors = {};
    FIELD_CONFIG.forEach((f) => {
      const raw = fieldValues[f.key];
      if (raw === '' || raw === undefined) {
        errors[f.key] = 'This field is required.';
        return;
      }
      const num = parseFloat(raw);
      if (isNaN(num)) {
        errors[f.key] = 'Must be a valid number.';
        return;
      }
      if (num < f.min || num > f.max) {
        errors[f.key] = `Must be between ${f.min}${f.unit} and ${f.max}${f.unit}.`;
      }
    });

    // Cross-field: (verification_threshold - under_review_margin) * 100 >= 50
    const vt = parseFloat(fieldValues['verification_threshold']);
    const urm = parseFloat(fieldValues['under_review_margin']);
    if (!isNaN(vt) && !isNaN(urm)) {
      if (vt - urm < 50) {
        errors['under_review_margin'] =
          'Verification threshold minus under-review margin cannot go below 50 %. Lower the margin or raise the threshold.';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // -------------------------------------------------------------------------
  // Save handler
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (!validateFields()) {
      showSnackbar('Please fix the highlighted errors before saving.', 'error');
      return;
    }
    setSaving(true);
    const payload = {};
    FIELD_CONFIG.forEach((f) => {
      payload[f.key] = toApiValue(f, fieldValues[f.key]);
    });
    payload.diagnostic_submission_mode = diagnosticSubmissionMode;

    const result = await faceRecognitionConfigService.updateConfig(payload);
    setSaving(false);

    if (result.success) {
      applyApiData(result.data);
      showSnackbar('Configuration saved successfully.', 'success');
    } else {
      showSnackbar(result.error, 'error');
    }
  };

  // -------------------------------------------------------------------------
  // Reset handler
  // -------------------------------------------------------------------------
  const handleResetConfirm = async () => {
    setResetDialogOpen(false);
    setResetting(true);
    const result = await faceRecognitionConfigService.resetConfig();
    setResetting(false);

    if (result.success) {
      applyApiData(result.data);
      showSnackbar('Configuration has been reset to factory defaults.', 'success');
    } else {
      showSnackbar(result.error, 'error');
    }
  };

  // -------------------------------------------------------------------------
  // Skeleton loader
  // -------------------------------------------------------------------------
  if (loading) {
    return (
      <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
        <Skeleton variant="rectangular" height={64} sx={{ borderRadius: 2, mb: 3 }} />
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1.5, mb: 2, maxWidth: 320 }} />
        <Skeleton variant="rectangular" height={360} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <Box sx={{ padding: { xs: 2, sm: 2.5, md: 3 }, backgroundColor: '#F8FAFC', minHeight: '100vh' }}>

      {/* ── Page Header ────────────────────────────────────────────────────── */}
      <Paper
        elevation={0}
        sx={{
          padding: { xs: 1.5, sm: 2, md: 2.5 },
          mb: { xs: 2, md: 3 },
          borderRadius: 2,
          background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <TuneIcon sx={{ color: 'white', fontSize: 28 }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, fontSize: { xs: '16px', sm: '20px' }, color: 'white', lineHeight: 1.2 }}
            >
              Manage Life Declaration Process
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', mt: 0.25 }}>
              Configure face recognition parameters for the life declaration flow.
            </Typography>
          </Box>
          {metaInfo.isActive !== null && (
            <Chip
              icon={metaInfo.isActive ? <CheckCircleIcon sx={{ color: '#059669 !important' }} /> : undefined}
              label={metaInfo.isActive ? 'Active Config' : 'Inactive'}
              size="small"
              sx={{
                backgroundColor: metaInfo.isActive ? '#D1FAE5' : '#FEE2E2',
                color: metaInfo.isActive ? '#059669' : '#DC2626',
                fontWeight: 600,
                fontSize: '12px',
              }}
            />
          )}
        </Box>
      </Paper>

      {/* ── Last-Updated Banner ──────────────────────────────────────────────── */}
      {metaInfo.updatedAt && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 2.5,
            px: 2,
            py: 1,
            borderRadius: 1.5,
            backgroundColor: '#EFF6FF',
            border: '1px solid #BFDBFE',
          }}
        >
          <InfoIcon sx={{ color: '#3B82F6', fontSize: 18 }} />
          <Typography sx={{ fontSize: '13px', color: '#1E40AF' }}>
            Last updated: <strong>{new Date(metaInfo.updatedAt).toLocaleString()}</strong>
            {metaInfo.updatedBy && (
              <> &nbsp;by <strong>{metaInfo.updatedBy}</strong></>
            )}
          </Typography>
        </Box>
      )}

      {/* ── Parameters Table (Desktop) / Cards (Mobile) ──────────────────── */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', overflow: 'hidden', mb: 3 }}>

        {/* Section sub-header */}
        <Box
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            backgroundColor: '#F8FAFC',
            borderBottom: '1px solid #E2E8F0',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <TuneIcon sx={{ color: '#4E83BD', fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1E293B' }}>
            Face Recognition Parameters
          </Typography>
        </Box>

        {/* ── Desktop Table ── */}
        {!isMobile && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeadCellSx, width: '25%' }}>Parameter Name</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: '50%' }}>Description</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: '25%' }}>Current Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {FIELD_CONFIG.map((field, idx) => (
                <TableRow
                  key={field.key}
                  sx={{
                    backgroundColor: idx % 2 === 0 ? 'white' : '#F8FAFC',
                    '&:hover': { backgroundColor: '#EFF6FF' },
                    verticalAlign: 'top',
                  }}
                >
                  {/* Parameter Name */}
                  <TableCell sx={{ py: 2.5, pr: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#1E293B', lineHeight: 1.4 }}>
                      {field.label}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#94A3B8', mt: 0.4 }}>
                      {field.key}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#1D4ED8', mt: 0.5, fontWeight: 600 }}>
                      Range: {field.constraintLabel}
                    </Typography>
                  </TableCell>

                  {/* Description */}
                  <TableCell sx={{ py: 2.5 }}>
                    <Typography sx={{ fontSize: '13px', color: '#475569', lineHeight: 1.6 }}>
                      {field.description}
                    </Typography>
                  </TableCell>

                  {/* Editable Value */}
                  <TableCell sx={{ py: 2 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={fieldValues[field.key] ?? ''}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      error={!!fieldErrors[field.key]}
                      helperText={fieldErrors[field.key] || ''}
                      inputProps={{ min: field.min, max: field.max, step: field.step }}
                      InputProps={
                        field.unit
                          ? { endAdornment: <InputAdornment position="end">{field.unit}</InputAdornment> }
                          : undefined
                      }
                      sx={{ width: '140px', ...inputStyles }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* ── Mobile Cards ── */}
        {isMobile && (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {FIELD_CONFIG.map((field, idx) => (
              <Card
                key={field.key}
                elevation={0}
                sx={{
                  border: '1px solid #E2E8F0',
                  borderRadius: 2,
                  backgroundColor: idx % 2 === 0 ? 'white' : '#F8FAFC',
                }}
              >
                <CardContent sx={{ pb: '12px !important' }}>
                  {/* Name row */}
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1E293B' }}>
                      {field.label}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#94A3B8', mt: 0.25 }}>
                      {field.key}
                    </Typography>
                    <Typography sx={{ fontSize: '11px', color: '#1D4ED8', mt: 0.4, fontWeight: 600 }}>
                      Range: {field.constraintLabel}
                    </Typography>
                  </Box>

                  {/* Description */}
                  <Typography sx={{ fontSize: '12px', color: '#475569', lineHeight: 1.6, mb: 1.5 }}>
                    {field.description}
                  </Typography>

                  <Divider sx={{ mb: 1.5 }} />

                  {/* Value input */}
                  <Box>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#64748B', mb: 0.75 }}>
                      Current Value
                    </Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={fieldValues[field.key] ?? ''}
                      onChange={(e) => handleFieldChange(field.key, e.target.value)}
                      error={!!fieldErrors[field.key]}
                      helperText={fieldErrors[field.key] || ''}
                      inputProps={{ min: field.min, max: field.max, step: field.step }}
                      InputProps={
                        field.unit
                          ? { endAdornment: <InputAdornment position="end">{field.unit}</InputAdornment> }
                          : undefined
                      }
                      sx={{ width: '160px', ...inputStyles }}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Paper>

      {/* ── Diagnostic Submission Mode Toggle ────────────────────────────── */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid #E2E8F0',
          mb: 3,
        }}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            justifyContent="space-between"
          >
            <Box sx={{ flex: 1, pr: { sm: 2 } }}>
              <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#1E293B' }}>
                Diagnostic Submission Mode
              </Typography>
              <Typography sx={{ fontSize: '12px', color: '#64748B', mt: 0.5, lineHeight: 1.6 }}>
                Relaxes cycle locks for operational diagnostics. Only enable in
                non-production environments.
              </Typography>
            </Box>
            <Switch
              checked={diagnosticSubmissionMode}
              onChange={(e) => setDiagnosticSubmissionMode(e.target.checked)}
              color="primary"
            />
          </Stack>
          {diagnosticSubmissionMode && (
            <Alert
              severity="warning"
              icon={<WarningIcon />}
              sx={{ mt: 2, fontSize: '12px', alignItems: 'center' }}
            >
              Diagnostic Submission Mode is ON. Users can submit outside the
              scheduled window. Disable before going to production.
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* ── Action Buttons ─────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <ActionButton
          variant="contained"
          onClick={handleSave}
          loading={saving}
          loadingText="Saving..."
          startIcon={<SaveIcon />}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #4E83BD 0%, #3A6A99 100%)',
            color: 'white',
            borderRadius: '10px',
            padding: '10px 24px',
            boxShadow: '0 4px 12px rgba(78, 131, 189, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #3A6A99 0%, #2D5580 100%)',
              boxShadow: '0 6px 16px rgba(78, 131, 189, 0.4)',
            },
          }}
        >
          Save Changes
        </ActionButton>

        <ActionButton
          variant="outlined"
          onClick={() => setResetDialogOpen(true)}
          loading={resetting}
          loadingText="Resetting..."
          startIcon={<RestartAltIcon />}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 600,
            color: '#DC2626',
            borderColor: '#DC2626',
            borderRadius: '10px',
            padding: '10px 24px',
            '&:hover': {
              backgroundColor: '#FEE2E2',
              borderColor: '#B91C1C',
            },
          }}
        >
          Reset to Defaults
        </ActionButton>
      </Box>

      {/* ── Reset Confirmation Dialog ─────────────────────────────────────── */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        {/* Gradient header */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <WarningIcon sx={{ color: 'white', fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '16px', color: 'white', lineHeight: 1.2 }}>
              Reset to Factory Defaults?
            </Typography>
            <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', mt: 0.25 }}>
              This action cannot be undone
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
          <DialogContentText sx={{ color: '#475569', fontSize: '14px', lineHeight: 1.7 }}>
            This will restore all face recognition parameters to their original factory defaults.
            Any custom values you have configured will be <strong>overwritten</strong>.
          </DialogContentText>

          {/* Warning note */}
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1.5,
              backgroundColor: '#FEF3C7',
              border: '1px solid #FCD34D',
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <WarningIcon sx={{ color: '#D97706', fontSize: 18, mt: 0.1, flexShrink: 0 }} />
            <Typography sx={{ fontSize: '12px', color: '#92400E', lineHeight: 1.5 }}>
              Changes will immediately affect the live life declaration flow for all users.
            </Typography>
          </Box>
        </DialogContent>

        {/* Actions */}
        <DialogActions sx={{ px: 3, py: 2, gap: 1.5 }}>
          <Button
            onClick={() => setResetDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              borderRadius: '8px',
              color: '#475569',
              borderColor: '#CBD5E1',
              px: 2.5,
              '&:hover': { borderColor: '#94A3B8', backgroundColor: '#F8FAFC' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleResetConfirm}
            variant="contained"
            startIcon={<RestartAltIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              borderRadius: '8px',
              px: 2.5,
              background: 'linear-gradient(135deg, #DC2626 0%, #B91C1C 100%)',
              boxShadow: '0 4px 12px rgba(220, 38, 38, 0.35)',
              '&:hover': {
                background: 'linear-gradient(135deg, #B91C1C 0%, #991B1B 100%)',
                boxShadow: '0 6px 16px rgba(220, 38, 38, 0.45)',
              },
            }}
          >
            Yes, Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageLifeDeclarationProcess;
